import { useCallback, useMemo, useState } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import { useReference } from '@/lib/reference/use-reference'
import { type AllowRow, type CityPair } from '@/lib/reference'
import {
  cityPairLegs,
  exceptionIndex,
  fleetColumns,
  resolveCityPair,
  verdictCounts,
} from '@/lib/reference/fleet'
import { ReferenceTable, type Column } from '@/components/reference/ReferenceTable'
import { CapabilitySummary } from '@/components/reference/CapabilitySummary'
import { ExceptionDialog } from '@/components/reference/ExceptionDialog'

const SHAPE_LABEL: Record<string, string> = {
  oneway: 'One-way',
  return: 'Return',
  return_techstop: 'Return · techstop',
}

/**
 * City pairs, keyed by their station sequence.
 *
 * The key is the sequence itself — SGN-PER-SGN for a plain return,
 * SGN-DPS-PER-DPS-SGN when each direction stops once — so a leg on the Gantt
 * can be matched against it without a second lookup table, and a pair that
 * gains a techstop becomes a different pair rather than a silently changed
 * one.
 *
 * Which aircraft may work a pair is not held here — it is a row of the
 * Capability matrix, addressed by this key, falling through to DEFAULT when a
 * pair has no row of its own. The Aircraft column reads that back with any
 * ad-hoc exception applied; editing it here makes an exception rather than
 * changing the rule.
 */
export function CityPairsPage() {
  const { can } = useAuth()
  const {
    cityPairs,
    cityPairCapability,
    capabilityException,
    aircraft,
    airports,
    create,
    update,
    remove,
  } = useReference()
  const [exception, setException] = useState<CityPair | null>(null)

  const editable = can('settings.edit')

  const knownStations = useMemo(
    () => new Set(airports.map((row) => row.iata).filter(Boolean)),
    [airports],
  )

  const fleet = useMemo(() => fleetColumns(aircraft), [aircraft])
  const exceptions = useMemo(
    () => exceptionIndex(capabilityException, 'cityPair'),
    [capabilityException],
  )

  const baselineFor = useCallback(
    (key: string) => (column: string) =>
      resolveCityPair(cityPairCapability, undefined, key, column).state,
    [cityPairCapability],
  )

  const capabilityOf = useCallback(
    (key: string) =>
      verdictCounts(fleet, (column) =>
        resolveCityPair(cityPairCapability, exceptions.get(key), key, column),
      ),
    [fleet, cityPairCapability, exceptions],
  )

  async function saveException(key: string, allow: AllowRow, reason: string) {
    const existing = exceptions.get(key)
    if (Object.keys(allow).length === 0) {
      if (existing) await remove('capabilityException', existing.id)
      return
    }
    if (existing) await update('capabilityException', existing.id, { allow, reason })
    else await create('capabilityException', { scope: 'cityPair', subject: key, allow, reason })
  }

  const columns = useMemo<Column<CityPair>[]>(
    () => [
      {
        key: 'key',
        header: 'Key',
        value: (row) => row.key,
        cellClassName: 'w-52',
        render: (row) => <span className="t-number-sm text-ink">{row.key}</span>,
      },
      {
        key: 'shape',
        header: 'Shape',
        value: (row) => row.shape,
        render: (row) => SHAPE_LABEL[row.shape] ?? row.shape,
        filter: 'select',
        editor: 'select',
        options: Object.keys(SHAPE_LABEL),
        cellClassName: 'w-40',
      },
      {
        key: 'origin',
        header: 'Origin',
        value: (row) => row.origin,
        editor: 'text',
        isKey: true,
        cellClassName: 'w-24',
        render: (row) => <span className="t-number-sm text-ink">{row.origin}</span>,
      },
      {
        key: 'turnaround',
        header: 'Turnaround',
        value: (row) => row.turnaround,
        editor: 'text',
        isKey: true,
        cellClassName: 'w-28',
        render: (row) => <span className="t-number-sm text-ink">{row.turnaround}</span>,
      },
      {
        key: 'outboundVia',
        header: 'Out via',
        value: (row) => row.outboundVia,
        editor: 'text',
        isKey: true,
        cellClassName: 'w-24',
        render: (row) =>
          row.outboundVia ? (
            <span className="t-number-sm text-ink">{row.outboundVia}</span>
          ) : (
            <span className="text-muted-soft">—</span>
          ),
      },
      {
        key: 'inboundVia',
        header: 'In via',
        value: (row) => row.inboundVia,
        editor: 'text',
        isKey: true,
        cellClassName: 'w-24',
        render: (row) =>
          row.inboundVia ? (
            <span className="t-number-sm text-ink">{row.inboundVia}</span>
          ) : (
            <span className="text-muted-soft">—</span>
          ),
      },
      {
        key: 'legs',
        header: 'Legs',
        value: (row) => cityPairLegs(row).length,
        numeric: true,
        filter: 'none',
        cellClassName: 'w-20 text-right',
      },
      {
        // Read off the Capability matrix: this pair's own row when it has
        // one, otherwise the DEFAULT row, exception applied on top.
        key: 'capability',
        header: 'Aircraft',
        value: (row) => capabilityOf(row.key).yes,
        numeric: true,
        filter: 'none',
        cellClassName: 'w-44',
        render: (row) => (
          <CapabilitySummary
            counts={capabilityOf(row.key)}
            onEdit={editable ? () => setException(row) : undefined}
          />
        ),
      },
      {
        key: 'tails',
        header: 'Tails',
        value: (row) => capabilityOf(row.key).tails,
        numeric: true,
        filter: 'none',
        cellClassName: 'w-20 text-right',
      },
    ],
    [capabilityOf, editable],
  )

  function issueOf(pair: CityPair): string | null {
    const unknown = cityPairLegs(pair)
      .flat()
      .filter((station) => !knownStations.has(station))
    if (unknown.length) return `Station not in table: ${[...new Set(unknown)].join(' ')}`
    return null
  }

  return (
    <section>
      <h1 className="t-title-lg text-ink">City pairs</h1>

      <div className="mt-6">
        <ReferenceTable
          columns={columns}
          rows={cityPairs}
          entity="City pair"
          canEdit={editable}
          // Source order: the narrow routes come first, which the alphabet
          // would scatter.
          defaultSortKey={null}
          rowWarning={issueOf}
          onCreate={(values) => create('cityPair', fieldsFrom(values))}
          onUpdate={(id, values) => update('cityPair', id, fieldsFrom(values))}
          onDelete={(id) => remove('cityPair', id)}
        />
      </div>

      {exception && (
        <ExceptionDialog
          subject={exception.key}
          columns={fleet}
          baseline={baselineFor(exception.key)}
          current={exceptions.get(exception.key)}
          canEdit={editable}
          onClose={() => setException(null)}
          onSave={(allow, reason) => saveException(exception.key, allow, reason)}
        />
      )}
    </section>
  )
}

function fieldsFrom(values: Record<string, string>) {
  const origin = values.origin.trim().toUpperCase()
  const turnaround = values.turnaround.trim().toUpperCase()
  const outboundVia = (values.outboundVia ?? '').trim().toUpperCase()
  const inboundVia = (values.inboundVia ?? '').trim().toUpperCase()
  // A stop on either leg makes it a techstop return, whatever was picked.
  const shape =
    outboundVia || inboundVia
      ? 'return_techstop'
      : ((values.shape as 'oneway' | 'return') || 'return')

  const stations = [origin]
  if (outboundVia) stations.push(outboundVia)
  stations.push(turnaround)
  if (shape !== 'oneway') {
    if (inboundVia) stations.push(inboundVia)
    stations.push(origin)
  }

  return {
    key: stations.join('-'),
    shape: shape as 'oneway' | 'return' | 'return_techstop',
    origin,
    turnaround,
    outboundVia,
    inboundVia,
  }
}
